import { EstateType } from "@1games-ru/region-shared/dist/enums/estate/estate";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import formatPrice from "@/core/helpers/formatPrice";
import { handleNumericChange } from "@/core/helpers/handleNumericChange";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RubleIcon } from "@/shared/icons";
import { Button, Checkbox, Modal, TextInput } from "@/shared/ui";
import Loader from "@/shared/ui/Loader";
import OfferView from "@/shared/widgets/OfferView";
import { offerCreatorSelector } from "@/domains/offer/slice";
import getEstateImage from "@/domains/estate/helpers/getEstateImage";
import useOfferCreatorEstate from "@/domains/offer/hooks/useOfferCreatorEstate";
import OfferEstateBuyer from "@/domains/offer/components/estate/OfferEstateBuyer";
import OfferEstateInfo from "@/domains/offer/components/estate/OfferEstateInfo";
import OfferEstateLocation from "@/domains/offer/components/estate/OfferEstateLocation";

export default function OfferEstateCreatorContainer({ isShow }: { isShow: boolean }) {
  const { t } = useTranslation("offer");

  const offerCreator = useAppSelector(offerCreatorSelector);

  const [price, setPrice] = useState("");
  const [isAgree, setIsAgree] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const { ownerEstates: estates, create, close, pending, loading } = useOfferCreatorEstate(isShow);

  const currentEstate = estates[activeIndex];

  const level = estates.length > 0 ? t(`estate:estate.level.${currentEstate.level}`) : "";

  const complexId =
    currentEstate?.entity && currentEstate.entity.type === EstateType.Apartment
      ? currentEstate?.entity?.content?.complexId
      : null;

  const src =
    estates.length > 0
      ? getEstateImage(
          { id: currentEstate?.id, type: currentEstate?.entity?.type, complexId },
          "medium",
        )
      : "";

  const flatNumber =
    currentEstate?.entity && currentEstate.entity.type === EstateType.Apartment
      ? `, ${t("estate:estate.flatNum")}.${currentEstate.entity.content.room}`
      : "";

  const address =
    estates.length > 0
      ? `${t("offer.saintPetersburg")}, ${currentEstate?.address?.streetName} ${currentEstate?.address?.index}${flatNumber}`
      : "";

  const onSubmit = useCallback(() => {
    return create(
      offerCreator.targetCharacter?.id,
      currentEstate?.id,
      currentEstate?.price,
      parseInt(price, 10),
    );
  }, [create, currentEstate, offerCreator?.targetCharacter?.id, price]);

  const toggleAgree = useCallback(() => {
    setIsAgree((state) => !state);
  }, []);

  const handleInputChange = (value: string) => handleNumericChange(value, setPrice);

  const isDisabled = useMemo(() => !price || !isAgree, [price, isAgree]);

  const notEmpty = !pending && !loading && estates?.length > 0;

  useEffect(() => {
    if (!isShow) {
      setActiveIndex(0);
      setPrice("");
      setIsAgree(false);
    }
  }, [isShow]);

  const footer = (
    <Button
      className={clsx("h-11 mr-1", !notEmpty && "!opacity-0 pointer-events-none")}
      onClick={onSubmit}
      disabled={isDisabled}
      loading={pending}
    >
      {t("offer.accept")}
    </Button>
  );

  const leftText = (
    <div className="flex items-center gap-2.5">
      <RubleIcon size={18} />
      <div className="flex flex-col gap-0.5">
        <p className="text-14 font-bold">{formatPrice(currentEstate?.price)} ₽</p>
        <p className="text-11 font-medium uppercase">{t("offer.estate.info.price")}</p>
      </div>
    </div>
  );

  const rightText = (
    <div className="flex flex-col gap-0.5 items-end ml-auto">
      <p className="text-14 font-bold">{formatPrice(currentEstate?.baseFee)} ₽</p>
      <p className="text-11 font-medium uppercase">{t("offer.estate.info.baseFee")}</p>
    </div>
  );

  return (
    <Modal
      open={isShow}
      title={t("offer.estate.creator.title")}
      onClose={close}
      footer={footer}
      className="w-[30rem] pt-6.5 pb-7.5 pl-7.5 pr-6.5"
      classNameCloseBtn="top-6.5 right-7.5"
      style={{ fontFamily: "TT Norms Pro" }}
    >
      <div className="relative flex flex-col min-h-[37.75rem]">
        {!notEmpty || pending || loading ? (
          <div className="absolute top-0 left-0 z-3 flex justify-center items-center size-full text-16 font-bold text-white/50 text-center leading-none uppercase">
            {!pending && !loading && t("offer.empty")}
            {(pending || loading) && <Loader />}
          </div>
        ) : (
          <>
            {offerCreator && offerCreator.targetCharacter && (
              <OfferEstateBuyer
                name={offerCreator.targetCharacter.name}
                avatar={offerCreator.targetCharacter.avatar}
                id={offerCreator.targetCharacter.id}
              />
            )}
            {estates && (
              <>
                <div>
                  <p className="mt-4 mb-3.5 text-16 font-medium">
                    {t("offer.estate.creator.estate")}:
                  </p>
                  <OfferView
                    src={src}
                    level={level}
                    list={estates || []}
                    activeIndex={activeIndex}
                    setActiveIndex={setActiveIndex}
                    leftText={leftText}
                    rightText={rightText}
                  />
                </div>
                <OfferEstateLocation address={address} />
                <OfferEstateInfo
                  livingCapacity={currentEstate?.livingCapacity}
                  garageCapacity={currentEstate?.garageCapacity}
                />
              </>
            )}
            <TextInput
              icon="₽"
              title={t("offer.price")}
              placeholder={t("offer.pricePlaceholder")}
              value={price}
              onChange={handleInputChange}
              maxLength={11}
              className="mt-5"
              isIconRight
            />
            <div className="flex items-center gap-2.5 mt-5">
              <Checkbox checked={isAgree} onChange={toggleAgree} />
              <button
                type="button"
                onClick={toggleAgree}
                className="text-14 font-medium text-inverted"
              >
                {t("offer.estate.creator.agree")}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
